import { atomWithStorage } from 'jotai/utils';
import { DateTime } from 'luxon';
import { WorkoutGroupedData, WorkoutWithCycleAndSession } from '../types';

export const workoutClipboardAtom = atomWithStorage<WorkoutClipboard | null>(
  'workoutClipboard',
  null,
);

export type WorkoutClipboard = {
  days: Array<{
    /** 0-based index of the copied day in selection order. */
    dateOffset: number;
    data: WorkoutGroupedData;
  }>;
};

/**
 * Builds a multi-day workout clipboard from an array of (date, grouped workout data) pairs.
 *
 * Selection order is preserved exactly — no date-based sorting is applied.
 * Paste always uses session-order matching rather than date arithmetic, so
 * two workouts copied from Jan 1 and Jan 10 will paste into the next two
 * consecutive sessions — not with a 9-day gap.
 */
export function buildWorkoutClipboard(
  days: Array<{ assignedDate: string | null; data: WorkoutGroupedData }>,
): WorkoutClipboard {
  return {
    days: days.map((d, i) => ({ dateOffset: i, data: d.data })),
  };
}

/**
 * Maps each clipboard day to the workout that should receive the paste,
 * using fill-forward arithmetic from targetAssignedDate.
 * Days that have no matching workout in prescriptionWorkouts are silently skipped.
 */
export function getMultiDayPasteTargets(
  clipboard: WorkoutClipboard,
  targetAssignedDate: string,
  prescriptionWorkouts: WorkoutWithCycleAndSession[],
): Array<{ clipboardData: WorkoutGroupedData; targetWorkoutId: string }> {
  const targetDate = DateTime.fromISO(targetAssignedDate);
  const results: Array<{
    clipboardData: WorkoutGroupedData;
    targetWorkoutId: string;
  }> = [];

  for (const { dateOffset, data } of clipboard.days) {
    const computedDate = targetDate.plus({ days: dateOffset }).toISODate();
    if (!computedDate) continue;

    const workout = prescriptionWorkouts.find(
      (w) => w.assignedDate === computedDate,
    );
    if (!workout) continue;

    results.push({ clipboardData: data, targetWorkoutId: workout.workoutId });
  }

  return results;
}

/**
 * Maps each clipboard day to the same session in consecutive cycles, starting
 * at the target workout's cycle. Used when the builder is in cycle group-by
 * view (columns = sessions, rows = cycles).
 *
 * Day 0 maps to the target workout (cycle C, session S), day 1 to the next
 * cycle (cycle C+1, session S), and so on.
 * If there are fewer remaining cycles than clipboard days, the extra clipboard
 * days are silently dropped.
 */
export function getMultiDayCycleGroupPasteTargetsByPosition(
  clipboard: WorkoutClipboard,
  targetWorkoutId: string,
  workouts: WorkoutWithCycleAndSession[],
): Array<{ clipboardData: WorkoutGroupedData; targetWorkoutId: string }> {
  const targetWorkout = workouts.find((w) => w.workoutId === targetWorkoutId);
  if (!targetWorkout) return [];

  // Derive sorted cycles from the workouts array (sorted by startDate).
  const cycleMap = new Map<number, WorkoutWithCycleAndSession['cycle']>();
  for (const w of workouts) {
    if (!cycleMap.has(w.cycle.id)) cycleMap.set(w.cycle.id, w.cycle);
  }
  const sortedCycles = Array.from(cycleMap.values()).sort(
    (a, b) => a.id - b.id,
  );

  const targetCycleIndex = sortedCycles.findIndex(
    (c) => c.id === targetWorkout.cycle.id,
  );
  if (targetCycleIndex === -1) return [];

  const targetSessionId = targetWorkout.session.id;
  const results: Array<{
    clipboardData: WorkoutGroupedData;
    targetWorkoutId: string;
  }> = [];

  for (let i = 0; i < clipboard.days.length; i++) {
    const clipboardDay = clipboard.days[i];
    if (!clipboardDay) continue;

    const targetCycle = sortedCycles[targetCycleIndex + i];
    if (!targetCycle) break;

    const targetW = workouts.find(
      (w) => w.cycle.id === targetCycle.id && w.session.id === targetSessionId,
    );
    if (!targetW) break;

    results.push({
      clipboardData: clipboardDay.data,
      targetWorkoutId: targetW.workoutId,
    });
  }

  return results;
}

/**
 * Maps each clipboard day to the next workout in session order within the same
 * cycle, starting at the target workout. Used as a fallback when workouts have
 * no assigned dates.
 *
 * Day 0 maps to the target workout, day 1 to the following session, and so on.
 * If there are fewer remaining workouts in the cycle than clipboard days, the
 * extra clipboard days are silently dropped.
 */
export function getMultiDayPasteTargetsByPosition(
  clipboard: WorkoutClipboard,
  targetWorkoutId: string,
  workouts: WorkoutWithCycleAndSession[],
): Array<{ clipboardData: WorkoutGroupedData; targetWorkoutId: string }> {
  const targetWorkout = workouts.find((w) => w.workoutId === targetWorkoutId);
  if (!targetWorkout) return [];

  const cycleWorkouts = workouts
    .filter((w) => w.cycle.id === targetWorkout.cycle.id)
    .sort((a, b) => a.session.id - b.session.id);

  const targetIndex = cycleWorkouts.findIndex(
    (w) => w.workoutId === targetWorkoutId,
  );
  if (targetIndex === -1) return [];

  const results: Array<{
    clipboardData: WorkoutGroupedData;
    targetWorkoutId: string;
  }> = [];

  for (let i = 0; i < clipboard.days.length; i++) {
    const clipboardDay = clipboard.days[i];
    if (!clipboardDay) continue;

    const targetW = cycleWorkouts[targetIndex + i];
    if (!targetW) break;

    results.push({
      clipboardData: clipboardDay.data,
      targetWorkoutId: targetW.workoutId,
    });
  }

  return results;
}
